import { Component, EventEmitter, inject, Injector, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as fromRoot from '../../../../../../../../../state/app.state';
import { projectDetails } from '../../../../../../../core/projects.selectors';
import { StationRangeMapGeometryService } from '../../../../../../../../../shared/services/station-range-map-geometry.service';
import { LocationPickerLauncherService } from '../../../../../../../../../shared/services/location-picker-launcher.service';
import {
  StationRangeComponent,
} from '../control-forms-details/station-range/station-range.component';
import { appDataDetails } from '../../../../../../../../../shared/core/shared.selectors';
import { MapFeatureLink } from '../../../../../../../../../shared/models/map-feature-link';
import type { FeatureSource } from '../../../../../../../../../shared/models/control-form';
import { linkCaptureImage, stationFeet } from '../../../../../../../../../shared/helpers/design-context';
import { confirmLinkRemoval } from '../../../../../../../../../shared/helpers/link-removal';
import { SheetMarkupQueueService } from '../../../../../../../../../shared/services/sheet-markup-queue.service';

@Component({
  selector: 'app-location-station-card',
  templateUrl: './location-station-card.component.html',
  styleUrls: ['./location-station-card.component.scss'],
  standalone: false,
})
export class LocationStationCardComponent implements OnInit, OnChanges, OnDestroy {
  private readonly injector = inject(Injector);
  @Input() latitude: any;
  @Input() longitude: any;
  @Input() locationText: any;
  @Input() stationRange: any;
  @Input() form: FormGroup;
  @Input() isEditable = true;
  @Input() hideLabel = false;
  @Input() useExternalClickHandler = false;
  /** "issue", "activity", …: the ArcGIS picker's "Link to this <record>" button. */
  @Input() recordLabel = 'record';
  /** 7.2: the record's sheet markup starts as this kind (an issue in an as-built category: As-built) */
  @Input() markupKind: 'note' | 'as_built' | null = null;
  /** The host form's ui_config.feature_source: the picker restricts/filters its layer and returns the feature's properties. */
  @Input() featureSource: FeatureSource | null = null;
  @Output() onDelete = new EventEmitter<Event>();
  @Output() cardClicked = new EventEmitter<void>();
  /** After Remove / Clear (`null`) or a snapshot retry (the updated link); the form control is already written. */
  @Output() mapFeatureLinkChange = new EventEmitter<MapFeatureLink | null>();

  normalizedStationRange: any = null;
  stationRangeMapData: any = null;
  appData: any = null;
  projectId: string | null = null;
  private alignmentGeometry: any = null;
  private readonly onDestroy = new Subject<void>();

  constructor(
    private store: Store<fromRoot.State>,
    private stationRangeGeometryService: StationRangeMapGeometryService,
    private dialog: MatDialog,
    private locationPickerLauncher: LocationPickerLauncherService,
  ) {}

  ngOnInit() {
    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.alignmentGeometry = project?.alignment_geometry || project?.alignmentGeometry;
        this.projectId = project?.id ?? null;
        this.updateDisplay();
      });

    this.store.select(appDataDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(appData => {
        this.appData = appData;
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.stationRange || changes.latitude || changes.longitude || changes.locationText) {
      this.updateDisplay();
    }
  }

  hasGpsLocation() {
    return this.hasCoordinateValue(this.latitude) && this.hasCoordinateValue(this.longitude);
  }

  hasStationRange() {
    return !!this.normalizedStationRange;
  }

  hasLocationOrStation() {
    // A sheet-only link (a pinless record marked on a drawing) is a location too.
    return this.hasGpsLocation() || !!this.locationText || this.hasStationRange() || !!this.mapFeatureLink?.sheet;
  }

  /** Strokes the picker holds for an unsaved sheet link; looked up only when needed (the queue is optional here) */
  private heldStrokes(link: MapFeatureLink): boolean {
    try {
      return !!this.injector.get(SheetMarkupQueueService).heldFor(link);
    } catch {
      return false;
    }
  }

  deleteLocation(event: Event) {
    event?.stopPropagation?.();
    // W77: the link goes too, so markup on it (held strokes, the record's sheet markup, a marked-up capture) asks first
    const link = this.mapFeatureLink;
    const sheet = link?.sheet;
    const held = !!sheet && !link.uuid && this.heldStrokes(link);
    confirmLinkRemoval(this.dialog, link, held).subscribe(go => {
      if (!go) return;
      // A link whose pin is gone would keep pointing at the old spot: clearing the location clears the link too.
      if (this.mapFeatureLink) this.writeMapFeatureLink(null);
      this.onDelete.emit(event);
    });
  }

  /**
   * The record's design context, read from the host's `map_feature_link` control.
   * The control only exists on projects with a published catalog or on records that
   * already carry a link, so a project without one shows nothing new (spec §10).
   */
  get mapFeatureLink(): MapFeatureLink | null {
    return this.form?.get('map_feature_link')?.value ?? null;
  }
  /** Spec addendum §11: the linked capture replaces the minimap (`linkCaptureImage`, the card's collapse test). */
  get hasCapture(): boolean { return !!linkCaptureImage(this.mapFeatureLink); }

  /** Clears the link, keeps the pin. */
  removeDesignContext(): void {
    if (!this.isEditable) return;
    this.writeMapFeatureLink(null);
  }

  /**
   * The control's server-side updates (a retry, a finished render, a saved markup). No valueChanges: hosts
   * push projectsService.isAnyFormDirty from them, and this is not an unsaved edit.
   */
  onDesignContextLinkChange(link: MapFeatureLink): void {
    this.form?.get('map_feature_link')?.setValue(link, { emitEvent: false });
    this.mapFeatureLinkChange.emit(link);
  }

  private writeMapFeatureLink(link: MapFeatureLink | null): void {
    const control = this.form?.get('map_feature_link');
    if (control) {
      control.setValue(link);
      control.markAsDirty();
      this.form.markAsDirty();
    }
    this.mapFeatureLinkChange.emit(link);
  }

  openCard(event?: Event) {
    event?.stopPropagation?.();
    if (this.useExternalClickHandler) {
      this.cardClicked.emit();
      return;
    }

    if (this.form && this.isEditable) {
      if (this.normalizedStationRange) {
        this.openStationRangePopup();
      } else {
        this.openLocationPickerDialog();
      }
    }
    this.cardClicked.emit();
  }

  private openStationRangePopup() {
    // Build a granular form pre-filled from the saved station range. The entity
    // parents (activity/delivery/issue/usage/control-forms) hold station_range
    // as a single object control and listen to dailyReportService.selectedLocation,
    // so we open StationRangeComponent with emitSelectedLocation so editing
    // round-trips correctly.
    const sr = this.normalizedStationRange || {};
    const form = new FormGroup({
      alignment: new FormControl(sr.alignment || null),
      stationRangeFromStation: new FormControl(sr.from?.station || null),
      stationRangeFromOffsetType: new FormControl(sr.from?.offsetType || null),
      stationRangeFromOffsetDistance: new FormControl(sr.from?.offsetDistance ?? null),
      stationRangeToStation: new FormControl(sr.to?.station || null),
      stationRangeToOffsetType: new FormControl(sr.to?.offsetType || null),
      stationRangeToOffsetDistance: new FormControl(sr.to?.offsetDistance ?? null),
    });

    this.dialog.open(StationRangeComponent, {
      disableClose: true,
      width: '1200px',
      height: '620px',
      maxWidth: 'calc(100vw - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      panelClass: 'station-range-dialog-panel',
      data: {
        form,
        emitSelectedLocation: true,
      },
    });
  }

  private openLocationPickerDialog() {
    this.locationPickerLauncher.open({
      latitude: this.latitude,
      longitude: this.longitude,
      location: this.locationText,
      // Hosts without a `location` control (activity codes, internal tests, testing forms) would discard the
      // text, and the picker's Done waits for it: no Location section there.
      enableLocationSection: !!this.form?.get('location'),
      showIncludeGpsLocationCheckbox: false,
      locationText: this.locationText || '',
      existingLink: this.form?.get('map_feature_link')?.value ?? null,
      recordLabel: this.recordLabel,
      markupKind: this.markupKind,
      station: stationFeet(this.stationRange?.from?.station),
      featureSource: this.featureSource,
      // Hosts without a link control (usage, external tests) would drop a picked link: Google picker there.
      designContext: !!this.form?.get('map_feature_link'),
    }).catch(error => console.error('Location picker failed to open', error));
  }

  private updateDisplay() {
    this.normalizedStationRange = this.stationRangeGeometryService.normalizeStationRange(this.stationRange);
    this.stationRangeMapData = this.buildStationRangeMapData();
  }

  private buildStationRangeMapData() {
    if (!this.normalizedStationRange) return null;

    const mapData = this.stationRangeGeometryService.buildStationRangeMapData(
      this.normalizedStationRange,
      this.alignmentGeometry,
    );
    if (!mapData) return null;

    const locationPoint = this.getLocationPoint();
    return {
      fromLabel: this.normalizedStationRange.from?.station,
      toLabel: this.normalizedStationRange.to?.station,
      geometry: this.buildFeatureCollection(mapData, locationPoint),
    };
  }

  private buildFeatureCollection(mapData: any, locationPoint: any = null) {
    const features = [];

    if (mapData.alignmentPath?.length >= 2) {
      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: mapData.alignmentPath.map(point => [point.lng, point.lat]),
          color: '#8EA0D8',
          opacity: 0.55,
          strokeWidth: 2,
          includeInExtent: false,
        },
      });
    }

    if (mapData.isSingleStation) {
      // FROM-only station: a dot on the alignment (moved to the offset when an
      // offset is set), plus a thin tie-line from the station to the offset.
      const station = mapData.singleStationPosition;
      const offset = mapData.fromOffsetPoint;
      if (station && offset) {
        features.push({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [[station.lng, station.lat], [offset.lng, offset.lat]],
            color: '#1036D6',
            strokeWidth: 2,
          },
        });
      }
      const dot = offset || station;
      if (dot) {
        features.push({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Point',
            coordinates: [dot.lng, dot.lat],
            color: '#1036D6',
            markerIconUrl: '/assets/icons/map/markers/offset-ring.svg',
            iconSize: { width: 12, height: 12, yOffset: 6 },
            includeInExtent: true,
          },
        });
      }
    }

    if (mapData.selectedPath?.length >= 2) {
      // Card mini-map: dashed range + book-end ticks (mobile parity). No offset
      // dots. Coarser dash + thinner stroke so it reads as dashed at the small
      // card zoom instead of looking solid.
      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: mapData.selectedPath.map(point => [point.lng, point.lat]),
          color: '#1036D6',
          dashTemplate: [12, 9],
          strokeWidth: 3,
        },
      });

      mapData.endCapPaths?.forEach(capPath => {
        features.push({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: capPath.map(point => [point.lng, point.lat]),
            color: '#1036D6',
            strokeWidth: 3,
          },
        });
      });
    }

    if (locationPoint) {
      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Point',
          coordinates: [locationPoint.lng, locationPoint.lat],
          color: '#1036D6',
          markerIconUrl: '/assets/icons/map/markers/location-pin.svg',
          iconSize: { width: 14, height: 19, yOffset: 9.5 },
          includeInExtent: true,
        },
      });
    }

    // Keep surrounding context on the mini-map instead of fitting tightly to
    // the range (mobile parity). Pad the extent proportional to the range span.
    const extentCoords = [
      ...(mapData.selectedPath || []),
      mapData.singleStationPosition,
      mapData.fromOffsetPoint,
      mapData.toOffsetPoint,
    ].filter(Boolean);
    if (extentCoords.length) {
      const lats = extentCoords.map(p => p.lat);
      const lngs = extentCoords.map(p => p.lng);
      const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
      const span = Math.max(Math.max(...lats) - Math.min(...lats), Math.max(...lngs) - Math.min(...lngs));
      const pad = Math.max(span * 0.9, 0.0006);
      // Invisible line (opacity 0) so nothing extra is drawn — only its
      // endpoints widen the extent for surrounding context.
      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [
            [centerLng - pad, centerLat - pad],
            [centerLng + pad, centerLat + pad],
          ],
          color: '#1036D6',
          opacity: 0,
          strokeWidth: 0,
          includeInExtent: true,
        },
      });
    }

    return {
      type: 'FeatureCollection',
      features,
    };
  }

  private getLocationPoint() {
    if (!this.hasGpsLocation()) return null;

    const lat = Number(this.latitude);
    const lng = Number(this.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng)
      ? { lat, lng }
      : null;
  }

  private hasCoordinateValue(value: any) {
    return value !== null && value !== undefined && `${value}`.trim() !== '';
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
